import {Component, effect, OnDestroy, Signal} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import {Router} from '@angular/router';
import {filter, startWith, take} from 'rxjs/operators';
import {Subscription, switchMap} from 'rxjs';
import {Organization} from '../../types-repository';
import {JanusService} from '../../services/janus.service';
import {MonitoringAccessService} from '../../services/monitoring-access.service';

class DummyOAuthEvent extends OAuthEvent {
  constructor() {
    super('silently_refreshed');
  }
}


@Component({
  selector: 'app-welcome',
  imports: [
    FormsModule,
    HeadlineSectionComponent,
    LogoAreaComponent,
    ReactiveFormsModule
  ],
  templateUrl: './welcome.component.html',
  styleUrl: './welcome.component.scss'
})
export class WelcomeComponent {
  loading = true;
  subscription: Subscription;
  organizations: Signal<Organization[] | undefined>;

  /** Guards against the effect landing the user twice if the organization list re-emits. */
  private landing = false;

  constructor(janus: JanusService, private router: Router, private monitoringAccess: MonitoringAccessService) {
    this.organizations = janus.organizations;
    effect(() => {
      const orgs = this.organizations()
      // Another navigation in flight means the user is already headed somewhere: after login the app
      // returns to the page they signed in from, and that navigation can still be waiting on the MFA
      // guard when the organization list arrives. Landing now would cancel it. Read as a signal, so
      // if that navigation fails and leaves the user here, this runs again and lands.
      if (!orgs || this.landing || this.router.currentNavigation()) {
        return;
      }
      this.landing = true;
      this.land(orgs);
    });

  }

  /**
   * Decide where a fresh login lands.
   *
   * Someone who can see platform monitoring gets it opened over their page on login, but only if they
   * have not looked at it in the last few hours -- so it interrupts when the numbers might have moved
   * rather than on every single login.
   *
   * Decided by policy, never by a staff flag: whoever holds the action gets it, and nothing in the
   * client names an organization.
   *
   * @param orgs The organizations the user belongs to.
   */
  private async land(orgs: Organization[]): Promise<void> {
    // Land on the page first, always. Monitoring is an overlay now, so it does not replace where the
    // user was going -- which also means nobody waits for the permission probe before seeing anything.
    this.landOnOrganization(orgs);

    // Checked before the probe, so a second login inside the window costs nothing at all.
    if (this.monitoringAccess.viewedRecently()) {
      return;
    }

    try {
      await this.monitoringAccess.resolve();
      if (this.monitoringAccess.organization()) {
        this.router.navigate([{outlets: {overlay: ['monitoring']}}], {queryParamsHandling: 'preserve'});
      }
    } catch {
      // A failed permission probe just means no overlay; the user is already on their page.
    }
  }

  /**
   * The ordinary landing: straight into a single organization, or the picker when there is a choice.
   *
   * @param orgs The organizations the user belongs to.
   */
  private landOnOrganization(orgs: Organization[]): void {
    if (orgs.length === 1) {
      this.router.navigate([{outlets: {primary: ['o', orgs[0].id], overlay: null}}]);
    } else if (orgs.length > 1) {
      this.router.navigate([{outlets: {overlay: ['organizations']}}]);
    }
  }

}
